// Day/night cycle. Drives sun direction + color, ambient, sky background,
// and pushes those values into the toon material registry so cel-shaded
// props re-shade as the sun moves.

import * as THREE from 'three';
import { updateToonLighting } from '../render/flat.js';

export function makeCycle(scene, {
  cycleSeconds = 60,
  keyLight = null,
  ambient = null,
  nightColor = new THREE.Color(0x1a1245),
  dawnColor = new THREE.Color(0x7a3a8e),
  dayColor = new THREE.Color(0x8a7ad6),
  duskColor = new THREE.Color(0xc04dc8),
  sunDawn = new THREE.Color(0xd76aff),
  sunNoon = new THREE.Color(0xf2dfff),
  sunDusk = new THREE.Color(0xff4dc8),
  sunMidnight = new THREE.Color(0xb8a6ff),
} = {}) {
  scene.fog = new THREE.FogExp2(nightColor, 0.0015);

  const sunDisc = new THREE.Mesh(
    new THREE.SphereGeometry(6, 24, 24),
    new THREE.MeshBasicMaterial({ color: sunNoon, transparent: true })
  );
  scene.add(sunDisc);

  // Night-biased schedule: most of the cycle is moonlit, brief dimmer day peak.
  // Day intensity capped at ~0.7 (half of original 1.4) — moonlight should dominate.
  const stops = [
    { t: 0.00, sky: nightColor, sun: sunMidnight, amb: 0.30, intensity: 0.22 },
    { t: 0.32, sky: nightColor, sun: sunMidnight, amb: 0.30, intensity: 0.22 },  // hold night
    { t: 0.40, sky: dawnColor,  sun: sunDawn,     amb: 0.38, intensity: 0.50 },
    { t: 0.50, sky: dayColor,   sun: sunNoon,     amb: 0.50, intensity: 0.70 },  // dimmer day peak
    { t: 0.60, sky: duskColor,  sun: sunDusk,     amb: 0.40, intensity: 0.55 },
    { t: 0.68, sky: nightColor, sun: sunMidnight, amb: 0.30, intensity: 0.22 },  // back to night
    { t: 1.00, sky: nightColor, sun: sunMidnight, amb: 0.30, intensity: 0.22 },
  ];

  const lerpStops = (t) => {
    let a = stops[0], b = stops[1];
    for (let i = 0; i < stops.length - 1; i++) {
      if (t >= stops[i].t && t <= stops[i+1].t) { a = stops[i]; b = stops[i+1]; break; }
    }
    const k = (t - a.t) / (b.t - a.t);
    return {
      sky: a.sky.clone().lerp(b.sky, k),
      sun: a.sun.clone().lerp(b.sun, k),
      amb: a.amb + (b.amb - a.amb) * k,
      intensity: a.intensity + (b.intensity - a.intensity) * k,
    };
  };

  // Sun direction (unit vector pointing AWAY from origin toward the sun)
  const sunDir = new THREE.Vector3();

  return {
    sunDisc,
    update(elapsed) {
      const tod = (elapsed % cycleSeconds) / cycleSeconds;
      const p = lerpStops(tod);
      scene.background = p.sky;
      scene.fog.color = p.sky;
      sunDisc.material.color.copy(p.sun);
      sunDisc.material.opacity = Math.min(1, Math.max(0, (p.intensity - 0.15)));

      // Sun moves along a tilted orbit. Big radius so it reads as "sky", not nearby.
      const angle = tod * Math.PI * 2 - Math.PI / 2;
      const r = 220, tilt = 0.25;
      sunDir.set(Math.cos(angle), Math.sin(angle) * (1 - tilt) + 0.1, Math.sin(angle) * tilt).normalize();
      sunDisc.position.copy(sunDir).multiplyScalar(r);

      // Drive the scene lights with the cycle.
      if (keyLight) {
        keyLight.position.copy(sunDir).multiplyScalar(50);
        keyLight.color.copy(p.sun);
        keyLight.intensity = p.intensity;
      }
      if (ambient) {
        const ambientCol = p.sky.clone().lerp(new THREE.Color(0xffffff), 0.25);
        ambient.color.copy(ambientCol);
        ambient.intensity = p.amb;
      }

      // Push into the toon registry so all cel-shaded materials update.
      const ambientCol = p.sky.clone().lerp(new THREE.Color(0xffffff), 0.25);
      updateToonLighting({
        sunDir,
        sunColor: p.sun,
        sunIntensity: p.intensity,
        ambientColor: ambientCol,
        ambientStrength: p.amb,
      });
      return tod;
    },
    getSunDir() { return sunDir; },
  };
}
